/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    background = "background",
}

export interface CustomRowStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
}

export const CustomRowStyles: CustomRowStyles = {
    wrapper: {
        css: css`
            margin: 0 auto;
            [class^="GridWrapper-sc-"] {
                margin: 0 auto;
            }
            div:first-child {
                max-width: 100% !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100% !important;
                        `,
                        css`
                            max-width: 720% !important;
                        `,
                        css`
                            max-width: 920px !important;
                        `,
                        css`
                            max-width: 1140px !important;
                        `,
                        css`
                            max-width: 1300px !important;
                        `,
                    ],
                    "min",
                )};
        `,
    },
    container: {
        css: css`
            max-width: 100%;
        `
    }
};

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.background]: string;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const styles = CustomRowStyles;

const CustomRow: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: { columns, gap, alignment, reflow, background },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    const overFlowWrapper = {
        css: css`
            background: ${background};
            width: calc(100vw - 20px + 6px);
            margin-left: calc(-50vw + 50% + 5px);
        `,
    };

    return (
        <StyledWrapper {...overFlowWrapper} className="main-container">
            <StyledWrapper {...styles.wrapper} className="content-wrapper">
                <GridContainer gap={gap || 0} {...styles.container}>{columnElements}</GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: CustomRow,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.background,
                displayName: "Component Background",
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
                sortOrder: 1,
            },
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
